import React, { useEffect, useId, useRef, useState } from 'react';
import { getFieldDisplayName } from '../formSchemaUtils';
import { Icon, sourceIconName } from './icons';
import {
  BORDERS,
  CONTACT_PART_OPTIONS,
  DATE_STYLES,
  FONT_SIZES,
  FORMATS,
  MARKS,
  boxFormats,
  clampRect,
  detailLabel,
  fromInches,
  isBlankBox,
  reformatBox,
  resizeType,
  toInches,
} from './templateLayout';

const SMALL_INPUT =
  'w-full rounded-lg border border-gray-600 bg-gray-700/50 px-3 py-2 text-sm text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent';
const FIELD_LABEL = 'block text-xs font-medium text-gray-400 mb-1.5';
const SMALL_BTN =
  'inline-flex items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium transition-colors';
const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.userAgent);
const MOD = IS_MAC ? '⌘' : 'Ctrl';
const ALT = IS_MAC ? 'Option' : 'Alt';

const Section = ({ title, children }) => (
  <section className="border-t border-gray-700/60 px-4 py-4 first:border-t-0">
    {title && (
      <h3 className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-gray-500">{title}</h3>
    )}
    {children}
  </section>
);

const Segmented = ({ label, options, value, onChange }) => (
  <div role="radiogroup" aria-label={label} className="inline-flex rounded-lg border border-gray-600 bg-gray-700/40 p-0.5">
    {options.map((option) => {
      const active = option.value === value;
      return (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={active}
          aria-label={option.title || option.label}
          title={option.title || option.label}
          onClick={() => onChange(option.value)}
          className={`inline-flex items-center justify-center rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors ${
            active
              ? 'bg-purple-500 text-white shadow'
              : 'bg-transparent text-gray-300 hover:bg-gray-600/60 hover:text-white'
          }`}
        >
          {option.icon ? <Icon name={option.icon} className="w-4 h-4" /> : option.label}
        </button>
      );
    })}
  </div>
);

const Toggle = ({ label, pressed, onClick, children }) => (
  <button
    type="button"
    aria-pressed={pressed}
    aria-label={label}
    title={label}
    onClick={onClick}
    className={`w-9 h-9 inline-flex items-center justify-center rounded-lg border p-0 text-sm transition-colors ${
      pressed
        ? 'bg-purple-500 border-purple-400 text-white'
        : 'bg-gray-700/40 border-gray-600 text-gray-300 hover:bg-gray-600/60 hover:text-white'
    }`}
  >
    {children}
  </button>
);

const Checkbox = ({ checked, onChange, children }) => (
  <label className="flex items-start gap-2 text-sm text-gray-300">
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      className="mt-0.5 accent-purple-500"
    />
    <span>{children}</span>
  </label>
);

// A measurement in inches. What's typed is kept as typed while the box has
// focus, so a value can be cleared and retyped; every number on the way is
// applied as it's typed, and the box shows the measurement again on blur.
const InchInput = ({ label, title, points, onCommit }) => {
  const [draft, setDraft] = useState(null);
  const id = useId();
  return (
    <label
      htmlFor={id}
      title={title}
      className="flex items-center gap-2 rounded-lg border border-gray-600 bg-gray-700/50 px-2.5 py-1.5 focus-within:ring-2 focus-within:ring-purple-500 focus-within:border-transparent"
    >
      <span className="w-3 text-xs font-semibold text-gray-500">{label}</span>
      <input
        id={id}
        type="number"
        inputMode="decimal"
        step="0.125"
        min="0"
        value={draft ?? toInches(points)}
        onChange={(e) => {
          setDraft(e.target.value);
          const inches = parseFloat(e.target.value);
          if (Number.isFinite(inches)) onCommit(fromInches(inches));
        }}
        onBlur={() => setDraft(null)}
        className="w-full min-w-0 bg-transparent p-0 text-sm text-white tabular-nums focus:outline-none"
      />
      <span className="text-xs text-gray-500">in</span>
    </label>
  );
};

// Nothing selected: the page as a whole — how far along it is, what still
// needs attention, and how the builder works.
const PagePanel = ({ fieldCount, placedFieldCount, issues, onSelectBox }) => (
  <>
    <Section>
      <h2 className="text-base font-semibold text-white">Page</h2>
      <p className="mt-1 text-sm text-gray-400">US Letter · 8½ × 11 in · Portrait</p>
      {fieldCount > 0 && (
        <div className="mt-4">
          <div className="flex items-center justify-between text-xs text-gray-400">
            <span>Questions on the page</span>
            <span className="tabular-nums text-gray-200">
              {placedFieldCount} of {fieldCount}
            </span>
          </div>
          <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-gray-700">
            <div
              className="h-full rounded-full bg-gradient-to-r from-purple-500 to-violet-400 transition-all duration-300"
              style={{ width: `${(placedFieldCount / fieldCount) * 100}%` }}
            />
          </div>
        </div>
      )}
    </Section>

    {issues.length > 0 && (
      <Section title={`Needs attention · ${issues.length}`}>
        <ul className="space-y-1.5">
          {issues.map(({ box, message }) => (
            <li key={box.id}>
              <button
                type="button"
                onClick={() => onSelectBox(box.id)}
                className="w-full flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/10 hover:bg-red-500/20 px-3 py-2 text-left text-sm text-red-100 transition-colors"
              >
                <Icon name="warning" className="w-4 h-4 mt-0.5 shrink-0 text-red-300" />
                <span>{message}</span>
              </button>
            </li>
          ))}
        </ul>
      </Section>
    )}

    <Section title="How it works">
      <ol className="space-y-3 text-sm text-gray-300">
        {[
          'Drag a question from the list onto the page — or click it to drop it in the next open spot.',
          'Or press on empty paper and drag to draw a box, then choose what goes in it.',
          'Click a box to choose how it prints: as a number, a percentage, a date, a note…',
          'Switch to Preview to see the page filled in with sample answers.',
        ].map((step, index) => (
          <li key={step} className="flex gap-3">
            <span className="flex items-center justify-center w-5 h-5 shrink-0 rounded-full bg-purple-500/20 text-[11px] font-semibold text-purple-200">
              {index + 1}
            </span>
            <span>{step}</span>
          </li>
        ))}
      </ol>
    </Section>

    <Section title="Shortcuts">
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-2 text-xs">
        {[
          ['Arrow keys', 'Nudge the selected box'],
          [`${ALT} + drag`, 'Move or size off the grid'],
          ['Enter', 'Change what fills a box'],
          [`${MOD} D`, 'Duplicate'],
          ['Delete', 'Remove'],
          [`${MOD} Z`, 'Undo'],
          [`${MOD} S`, 'Save'],
        ].map(([keys, action]) => (
          <React.Fragment key={keys}>
            <dt>
              <kbd className="rounded border border-gray-600 bg-gray-700/60 px-1.5 py-0.5 font-sans text-gray-200">{keys}</kbd>
            </dt>
            <dd className="text-gray-400">{action}</dd>
          </React.Fragment>
        ))}
      </dl>
    </Section>
  </>
);

// A box selected: what fills it, how it prints, how it's set, where it sits.
// Each control only appears where it means something — no decimal places on a
// date, no type size on a photo — which keeps the panel to what this one box
// can actually do.
const BoxPanel = ({ box, field, issue, focusText, onTextFocused, onChange, onOpenPicker, onDuplicate, onDelete }) => {
  const textRef = useRef(null);
  // A one-off request, handed back as soon as it's met so that coming back to
  // this box later doesn't pull the cursor out of the page again.
  useEffect(() => {
    if (!focusText) return;
    textRef.current?.focus();
    onTextFocused();
  }, [focusText, onTextFocused]);

  const blank = isBlankBox(box);
  const missing = box.kind === 'field' && !blank && !field;
  const formats = boxFormats(box, field);
  const printsText = box.kind === 'text' || !['photo', 'check'].includes(box.format);
  const numeric = ['number', 'percent', 'currency'].includes(box.format);
  const rect = { x: box.x, y: box.y, w: box.w, h: box.h };
  const fieldName = field ? getFieldDisplayName(field) : box.fieldName;

  let kind = 'Question';
  let title = fieldName || 'Missing question';
  if (box.kind === 'text') {
    kind = 'Your own text';
    title = 'Text';
  } else if (box.kind === 'detail') {
    kind = 'Submission detail';
    title = detailLabel(box.detail);
  } else if (blank) {
    kind = 'Empty box';
    title = 'Nothing chosen yet';
  }
  const iconSource = blank ? null : { kind: box.kind, field, detail: box.detail };

  return (
    <>
      <Section>
        <div className="flex items-start gap-3">
          <span className="flex items-center justify-center w-9 h-9 shrink-0 rounded-lg bg-purple-500/20 text-purple-200">
            <Icon name={iconSource ? sourceIconName(iconSource) : 'blank'} className="w-5 h-5" />
          </span>
          <div className="min-w-0">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">{kind}</div>
            <div className="text-base font-semibold text-white truncate" title={field?.label || title}>
              {title}
            </div>
          </div>
        </div>
        {issue && (
          <div className="mt-3 flex items-start gap-2 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-100">
            <Icon name="warning" className="w-4 h-4 mt-0.5 shrink-0 text-red-300" />
            <span>{issue}</span>
          </div>
        )}
        <button
          type="button"
          onClick={onOpenPicker}
          className={`mt-3 w-full ${SMALL_BTN} ${
            blank || missing
              ? 'border-transparent bg-gradient-to-r from-purple-500 to-violet-500 hover:from-purple-600 hover:to-violet-600 text-white shadow'
              : 'border-gray-600 bg-gray-700/50 hover:bg-gray-600/60 text-gray-200 hover:text-white'
          }`}
        >
          <Icon name="swap" className="w-4 h-4" />
          {blank || missing ? 'Choose what goes here' : 'Change what fills this box'}
        </button>
      </Section>

      {box.kind === 'text' && (
        <Section title="Text">
          <textarea
            ref={textRef}
            value={box.text || ''}
            onChange={(e) => onChange({ text: e.target.value }, 'text')}
            rows={3}
            maxLength={2000}
            placeholder="e.g. Roof Inspection Report"
            aria-label="Text printed in this box"
            className={`${SMALL_INPUT} resize-y`}
          />
        </Section>
      )}

      {!blank && !missing && box.kind !== 'text' && (
        <Section title="Prints as">
          <div className="space-y-3">
            {field?.type === 'contact' && (
              <div>
                <label className={FIELD_LABEL} htmlFor="ti-part">Which part of the contact</label>
                <select
                  id="ti-part"
                  value={box.part || 'all'}
                  onChange={(e) => onChange({ part: e.target.value })}
                  className={SMALL_INPUT}
                >
                  {CONTACT_PART_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
            )}

            {formats.length > 1 && (
              <div>
                <label className={FIELD_LABEL} htmlFor="ti-format">Show the answer as</label>
                <select
                  id="ti-format"
                  value={formats.includes(box.format) ? box.format : ''}
                  onChange={(e) => onChange((current) => reformatBox(current, e.target.value))}
                  className={SMALL_INPUT}
                >
                  {!formats.includes(box.format) && <option value="" disabled>Choose a format</option>}
                  {FORMATS.filter((format) => formats.includes(format.value)).map((format) => (
                    <option key={format.value} value={format.value}>{format.label}</option>
                  ))}
                </select>
                <p className="mt-1.5 text-xs text-gray-500">
                  {FORMATS.find((format) => format.value === box.format)?.hint}
                </p>
              </div>
            )}

            {numeric && (
              <>
                <div>
                  <label className={FIELD_LABEL} htmlFor="ti-decimals">Decimal places</label>
                  <select
                    id="ti-decimals"
                    value={Number.isInteger(box.decimals) ? String(box.decimals) : 'auto'}
                    onChange={(e) => onChange({ decimals: e.target.value === 'auto' ? null : Number(e.target.value) })}
                    className={SMALL_INPUT}
                  >
                    <option value="auto">{box.format === 'currency' ? 'Cents (2)' : 'As needed, up to 2'}</option>
                    {[0, 1, 2, 3, 4].map((count) => (
                      <option key={count} value={count}>{count}</option>
                    ))}
                  </select>
                </div>
                <Checkbox checked={box.grouping !== false} onChange={(on) => onChange({ grouping: on })}>
                  Separate thousands with commas (1,000)
                </Checkbox>
                {box.format === 'percent' && (
                  <Checkbox checked={!!box.fraction} onChange={(on) => onChange({ fraction: on })}>
                    The answer is a fraction (0.25 prints as 25%)
                  </Checkbox>
                )}
              </>
            )}

            {box.format === 'date' && (
              <div>
                <label className={FIELD_LABEL} htmlFor="ti-date">Date style</label>
                <select
                  id="ti-date"
                  value={box.dateStyle || 'short'}
                  onChange={(e) => onChange({ dateStyle: e.target.value })}
                  className={SMALL_INPUT}
                >
                  {DATE_STYLES.map((style) => (
                    <option key={style.value} value={style.value}>{style.label}</option>
                  ))}
                </select>
              </div>
            )}

            {box.format === 'check' && (
              <>
                <div>
                  <label className={FIELD_LABEL} htmlFor="ti-match">Tick it when the answer is</label>
                  <select
                    id="ti-match"
                    value={box.match || ''}
                    onChange={(e) => onChange({ match: e.target.value })}
                    className={SMALL_INPUT}
                  >
                    <option value="" disabled>Choose an answer</option>
                    {(field?.options || []).filter(Boolean).map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <span className={FIELD_LABEL}>Mark</span>
                  <Segmented
                    label="Mark"
                    options={MARKS}
                    value={box.mark || 'check'}
                    onChange={(mark) => onChange({ mark })}
                  />
                </div>
              </>
            )}

            {printsText && (
              <div>
                <label className={FIELD_LABEL} htmlFor="ti-label">Label printed before it</label>
                <input
                  id="ti-label"
                  type="text"
                  value={box.label || ''}
                  maxLength={200}
                  onChange={(e) => onChange({ label: e.target.value }, 'label')}
                  placeholder="None"
                  className={SMALL_INPUT}
                />
                {!box.label && (
                  <button
                    type="button"
                    onClick={() => onChange({ label: `${box.kind === 'detail' ? detailLabel(box.detail) : fieldName}:` })}
                    className="mt-1.5 bg-transparent hover:bg-transparent p-0 text-xs text-purple-300 hover:text-purple-200 hover:underline"
                  >
                    Use “{box.kind === 'detail' ? detailLabel(box.detail) : fieldName}:”
                  </button>
                )}
              </div>
            )}
          </div>
        </Section>
      )}

      {printsText && (
        <Section title="Type">
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={box.fontSize}
              onChange={(e) => onChange((current) => resizeType(current, Number(e.target.value)))}
              aria-label="Type size"
              title="Type size, in points"
              className="rounded-lg border border-gray-600 bg-gray-700/50 py-2 pl-3 text-sm text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              {(FONT_SIZES.includes(box.fontSize) ? FONT_SIZES : [...FONT_SIZES, box.fontSize].sort((a, b) => a - b)).map((size) => (
                <option key={size} value={size}>{size} pt</option>
              ))}
            </select>
            <Toggle label="Bold" pressed={!!box.bold} onClick={() => onChange({ bold: !box.bold })}>
              <span className="font-bold">B</span>
            </Toggle>
            <Toggle label="Italic" pressed={!!box.italic} onClick={() => onChange({ italic: !box.italic })}>
              <span className="italic font-serif">I</span>
            </Toggle>
            <Segmented
              label="Alignment"
              options={[
                { value: 'left', title: 'Align left', icon: 'alignLeft' },
                { value: 'center', title: 'Center', icon: 'alignCenter' },
                { value: 'right', title: 'Align right', icon: 'alignRight' },
              ]}
              value={box.align}
              onChange={(align) => onChange({ align })}
            />
          </div>
        </Section>
      )}

      <Section title="Border">
        <Segmented label="Border" options={BORDERS} value={box.border} onChange={(border) => onChange({ border })} />
      </Section>

      <Section title="Position and size">
        <div className="grid grid-cols-2 gap-2">
          <InchInput label="X" title="From the left edge" points={box.x} onCommit={(x) => onChange(clampRect({ ...rect, x }), 'x')} />
          <InchInput label="Y" title="From the top edge" points={box.y} onCommit={(y) => onChange(clampRect({ ...rect, y }), 'y')} />
          <InchInput label="W" title="Width" points={box.w} onCommit={(w) => onChange(clampRect({ ...rect, w }), 'w')} />
          <InchInput label="H" title="Height" points={box.h} onCommit={(h) => onChange(clampRect({ ...rect, h }), 'h')} />
        </div>
      </Section>

      <Section>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={onDuplicate}
            className={`${SMALL_BTN} border-gray-600 bg-gray-700/50 hover:bg-gray-600/60 text-gray-200 hover:text-white`}
          >
            <Icon name="copy" className="w-4 h-4" />
            Duplicate
          </button>
          <button
            type="button"
            onClick={onDelete}
            className={`${SMALL_BTN} border-red-500/30 bg-red-500/10 hover:bg-red-500/20 text-red-300 hover:text-red-200`}
          >
            <Icon name="trash" className="w-4 h-4" />
            Delete
          </button>
        </div>
      </Section>
    </>
  );
};

const TemplateInspector = ({ box, ...props }) =>
  box ? <BoxPanel key={box.id} box={box} {...props} /> : <PagePanel {...props} />;

export default TemplateInspector;
